export default function AboutSection() {
  return (
    <div className="overflow-hidden bg-white pt-16 px-4 sm:px-6 lg:px-8 xl:pt-20">
      <div className="mx-auto max-w-max lg:max-w-7xl lg:px-2">
        <div className="relative z-10 mb-8 md:mb-2 md:px-6">
          <div className="max-w-prose text-base lg:max-w-none">
            <h2 className="font-semibold text-2xl leading-6 text-neutral-600">
              Why rule engines?
            </h2>
            <p className="mt-4 text-4xl font-semibold leading-10 tracking-tight text-gray-900 sm:text-5xl">
              A better way to build business logic
            </p>
          </div>
        </div>
        <div className="relative">
          <svg
            className="absolute opacity-20 top-0 right-0 scale-125 -mr-20 hidden md:block md:[overflow-anchor:none]"
            width={404}
            height={384}
            fill="none"
            viewBox="0 0 404 384"
            aria-hidden="true"
          >
            <defs>
              <pattern
                id="95e8f2de-6d30-4b7e-8159-f791729db21b"
                x={0}
                y={0}
                width={20}
                height={20}
                patternUnits="userSpaceOnUse"
              >
                <rect
                  x={0}
                  y={0}
                  width={4}
                  height={4}
                  className="text-black"
                  fill="currentColor"
                />
              </pattern>
            </defs>
            <rect
              width={404}
              height={384}
              fill="url(#95e8f2de-6d30-4b7e-8159-f791729db21b)"
            />
          </svg>
          <svg
            className="absolute opacity-20 bottom-0 left-0 scale-125 -ml-20 hidden md:block md:[overflow-anchor:none]"
            width={404}
            height={384}
            fill="none"
            viewBox="0 0 404 384"
            aria-hidden="true"
          >
            <defs>
              <pattern
                id="7a00fe67-0343-4a3c-8e81-c145097a3ce0"
                x={0}
                y={0}
                width={20}
                height={20}
                patternUnits="userSpaceOnUse"
              >
                <rect
                  x={0}
                  y={0}
                  width={4}
                  height={4}
                  className="text-black"
                  fill="currentColor"
                />
              </pattern>
            </defs>
            <rect
              width={404}
              height={384}
              fill="url(#7a00fe67-0343-4a3c-8e81-c145097a3ce0)"
            />
          </svg>
          <div className="relative md:bg-white md:m-6 md:border border-neutral-400 border-opacity-60 my-4 md:my-12 rounded-md">
            <div className="lg:grid lg:grid-cols-2 lg:gap-6 md:p-6">
              <div className="prose prose-lg text-xl leading-9 prose-neutral text-black lg:max-w-none lg:p-6">
                <p>
                  If you've ever built or used a visual automation tool, you
                  know that there's always a limit to the logic you can put in.
                  Even the most simple <i>if/else</i> or <i>switch/case</i>{" "}
                  statements can get complicated quickly, and when you start
                  nesting them or modeling complex scenarios, things can get out
                  of control. For businesses that offer automation tools, this
                  complexity can be remarkably difficult to support, and for
                  users of automation tools, it can be tricky to understand how
                  to use the logic features across so many emerging platforms.
                </p>
                <p>
                  We built Rulebricks to solve this problem, after years of work
                  in RPA building our own digital automation software. We're
                  intimately familiar with the limitations of the built-in logic
                  in most automation platforms, and we've seen firsthand just
                  how much time and money businesses can waste trying to work
                  around those limits.
                </p>
              </div>
              <div className="prose prose-lg text-xl leading-9 prose-neutral mt-6 text-black lg:mt-0 lg:p-6">
                <p>
                  Rule engines don't just make it easier to build complex logic,
                  they also leave you with a completely different level of
                  visibility into your automations, and let you make changes to
                  particular decision points without having to redeploy your
                  entire process. If your automations have several paths that
                  can be taken, you'll be able to see exactly which path was
                  taken for each execution, and exactly why, equipping your team
                  with information critical to addressing failures and
                  optimizing your processes in the long run.
                </p>
                <p>
                  With automation arriving to more business functions than ever,
                  and with the explosion of no-code platforms to meet their
                  needs, we think there's a place for Rulebricks to help
                  businesses model big decisions and get the most out of tools
                  they already use.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-black mt-12 -mx-8">
        <div className="mx-auto max-w-xl py-16 px-4 sm:py-16 pb-16 sm:pb-24 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-1 lg:gap-8">
            <h2 className="mx-auto max-w-md text-center text-3xl font-semibold tracking-tight text-neutral-600 lg:max-w-xl lg:text-left">
              Built by engineers from
            </h2>
            <div className="mt-8 flow-root self-center lg:mt-0 mx-6 opacity-40">
              <div className="-mt-4 -ml-8 flex flex-wrap justify-between lg:-ml-4">
                <div className="mt-4 ml-8 flex flex-shrink-0 flex-grow justify-center lg:ml-4 lg:flex-grow-0">
                  <img
                    className="h-16 translate-y-11 translate-x-8 sm:translate-x-0 sm:translate-y-0 grayscale ring-neutral-500 ring p-0 aspect-square object-contain rounded"
                    src="/static/images/yc-logo.png"
                    alt="YCombinator"
                  />
                </div>
                <div className="mt-4 ml-8 flex flex-shrink-0 flex-grow justify-center lg:ml-4 lg:flex-grow-0">
                  <img
                    className="h-16 grayscale ring-neutral-500 ring p-2 aspect-square object-contain rounded"
                    src="/static/images/pioneer-logo.png"
                    alt="Pioneer"
                  />
                </div>
                <div className="mt-4 translate-y-11 sm:translate-y-0 -translate-x-8 sm:translate-x-0 ml-8 flex flex-shrink-0 flex-grow justify-center lg:ml-4 lg:flex-grow-0">
                  <img
                    className="h-16 grayscale ring-neutral-500 ring p-2 aspect-square object-contain invert rounded"
                    src="/static/images/ssc-logo.png"
                    alt="State Street"
                  />
                </div>
                <div className="mt-8 sm:mt-4 ml-8 flex flex-shrink-0 flex-grow justify-center lg:ml-4 lg:flex-grow-0">
                  <img
                    className="h-16 grayscale ring-neutral-500 ring p-2 aspect-square object-contain invert rounded"
                    src="/static/images/cai-logo.png"
                    alt="Crowd"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
